import React, { useState, useEffect, useMemo } from 'react';
import { X, Hash, Loader2, Check } from 'lucide-react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import { cn } from "@/lib/utils";

// shadcn/ui components
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogFooter,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    Command,
    CommandInput,
    CommandList,
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from "@/components/ui/command";

import { BACKEND_API_URL } from "../utils/api";
import { selectAllChannels } from "../redux/selectors/channel.selectors";

const REACT_APP_BACKEND_API_URL = BACKEND_API_URL;

const MAX_MESSAGES = 30;
const MAX_CHANNELS = 10;

const ForwardMessageDialog = ({ open, onOpenChange, selectedMessages, onForwardSuccess }) => {
    const allChannels = useSelector(selectAllChannels);
    const [selectedChannels, setSelectedChannels] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // Reset state when dialog opens/closes
    useEffect(() => {
        if (open) {
            setSelectedChannels([]);
            setSearchQuery('');
        }
    }, [open]);

    // Filter channels based on search query and exclude already selected
    const filteredChannels = useMemo(() => {
        const selectedIds = new Set(selectedChannels.map(c => c.id));
        return allChannels.filter(channel => {
            // Exclude already selected channels
            if (selectedIds.has(channel.id)) return false;
            // Exclude archived channels
            if (channel.is_archived) return false;
            // Filter by search query
            if (!searchQuery) return true;
            const name = (channel.name || '').toLowerCase();
            const description = (channel.description || '').toLowerCase();
            const query = searchQuery.toLowerCase();
            return name.includes(query) || description.includes(query);
        });
    }, [allChannels, selectedChannels, searchQuery]);

    const handleSelectChannel = (channel) => {
        if (selectedChannels.length >= MAX_CHANNELS) {
            toast.error(`Maximum ${MAX_CHANNELS} channels can be selected`);
            return;
        }
        setSelectedChannels(prev => [...prev, channel]);
        setSearchQuery('');
    };

    const handleRemoveSelectedChannel = (channelId) => {
        setSelectedChannels(prev => prev.filter(c => c.id !== channelId));
    };

    const handleForward = async () => {
        if (selectedChannels.length === 0) {
            toast.error('Please select at least one channel');
            return;
        }

        if (selectedMessages.length === 0) {
            toast.error('No messages selected');
            return;
        }

        if (selectedMessages.length > MAX_MESSAGES) {
            toast.error(`Maximum ${MAX_MESSAGES} messages can be forwarded`);
            return;
        }

        setIsLoading(true);
        try {
            const accessToken = Cookies.get('access_token');
            const messageIds = selectedMessages.map(msg => msg.id);
            const channelIds = selectedChannels.map(ch => ch.id);

            const response = await axios.post(
                `${REACT_APP_BACKEND_API_URL}/api/messages/forward/`,
                {
                    message_ids: messageIds,
                    channel_ids: channelIds,
                },
                {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.data.success) {
                toast.success(
                    `Forwarded ${selectedMessages.length} message(s) to ${selectedChannels.length} channel(s)`
                );
                setSelectedChannels([]);
                onOpenChange(false);
                if (onForwardSuccess) {
                    onForwardSuccess();
                }
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.error || 'Failed to forward messages';
            toast.error(errorMessage);
            console.error('Error forwarding messages:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setSelectedChannels([]);
            onOpenChange(false);
        }
    };

    const messageCount = selectedMessages.length;
    const channelCount = selectedChannels.length;
    const canSelectMoreChannels = channelCount < MAX_CHANNELS;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-xl max-h-[85vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="text-lg font-semibold">
                        Forward Messages
                    </DialogTitle>
                    <DialogDescription>
                        Forward {messageCount} selected message{messageCount !== 1 ? 's' : ''} to channel{channelCount !== 1 ? 's' : ''}
                        {messageCount > MAX_MESSAGES && (
                            <span className="text-destructive block mt-1">
                                Maximum {MAX_MESSAGES} messages allowed
                            </span>
                        )}
                    </DialogDescription>
                </DialogHeader>
                
                <ScrollArea className="flex-1 pr-2 -mr-2">
                    <div className="space-y-4 py-2">
                        {/* Selected Messages Preview */}
                        <div className="rounded-lg border border-border p-3">
                            <p className="text-xs text-muted-foreground mb-2">
                                Selected Messages ({messageCount}/{MAX_MESSAGES}):
                            </p>
                            <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                                {selectedMessages.slice(0, 10).map((message) => (
                                    <Badge
                                        key={message.id}
                                        variant="secondary"
                                        className="text-xs max-w-[140px] truncate"
                                    >
                                        {message.message?.substring(0, 20) || 'Media'}...
                                    </Badge>
                                ))}
                                {selectedMessages.length > 10 && (
                                    <Badge variant="outline" className="text-xs">
                                        +{selectedMessages.length - 10} more
                                    </Badge>
                                )}
                            </div>
                        </div>

                        {/* Selected Channels */}
                        {selectedChannels.length > 0 && (
                            <div className="rounded-lg bg-muted/50 p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm font-medium">
                                        Selected Channels ({channelCount}/{MAX_CHANNELS})
                                    </p>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {selectedChannels.map((channel) => (
                                        <div
                                            key={channel.id}
                                            className="inline-flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-full bg-primary text-primary-foreground"
                                        >
                                            <Hash className="w-3.5 h-3.5" />
                                            <span className="text-xs font-medium max-w-[150px] truncate">
                                                {channel.name}
                                            </span>
                                            <button
                                                onClick={() => handleRemoveSelectedChannel(channel.id)}
                                                disabled={isLoading}
                                                className="rounded-full p-0.5 hover:bg-primary-foreground/20 transition-colors disabled:opacity-50"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Channel Search/Select */}
                        <div className="space-y-2">
                            <p className="text-sm font-medium">
                                Select Channels to Forward To
                                {!canSelectMoreChannels && (
                                    <span className="text-destructive text-xs ml-2">
                                        (Maximum {MAX_CHANNELS} channels)
                                    </span>
                                )}
                            </p>
                            <Command 
                                className="rounded-lg border border-border overflow-hidden" 
                                shouldFilter={false}
                            >
                                <CommandInput
                                    placeholder="Search channels..."
                                    value={searchQuery}
                                    onValueChange={setSearchQuery}
                                    disabled={isLoading || !canSelectMoreChannels}
                                />
                                <CommandList className="min-h-[200px] max-h-[200px]">
                                    <CommandEmpty className="py-12 text-center text-sm text-muted-foreground">
                                        {!canSelectMoreChannels 
                                            ? `Maximum ${MAX_CHANNELS} channels selected`
                                            : 'No channels found.'
                                        }
                                    </CommandEmpty>
                                    <CommandGroup>
                                        {filteredChannels.slice(0, 20).map((channel) => (
                                            <CommandItem
                                                key={channel.id}
                                                value={channel.id.toString()}
                                                onSelect={() => handleSelectChannel(channel)}
                                                disabled={!canSelectMoreChannels}
                                                className="flex items-center gap-3 py-2.5 px-3 cursor-pointer"
                                            >
                                                <Hash className="w-4 h-4 shrink-0 text-muted-foreground" />
                                                <div className="flex flex-col min-w-0 flex-1">
                                                    <span className="text-sm font-medium truncate">
                                                        {channel.name}
                                                    </span>
                                                    {channel.description && (
                                                        <span className="text-xs text-muted-foreground truncate">
                                                            {channel.description}
                                                        </span>
                                                    )}
                                                </div>
                                                {selectedChannels.some(c => c.id === channel.id) && (
                                                    <Check className="w-4 h-4 shrink-0 text-primary" />
                                                )}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </div>
                    </div>
                </ScrollArea>

                <DialogFooter className="gap-2 sm:gap-0 pt-2">
                    <Button 
                        variant="outline" 
                        onClick={handleClose} 
                        disabled={isLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleForward}
                        disabled={selectedChannels.length === 0 || isLoading || messageCount > MAX_MESSAGES}
                    >
                        {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {isLoading 
                            ? 'Forwarding...' 
                            : `Forward to ${channelCount} channel${channelCount !== 1 ? 's' : ''}`
                        }
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default ForwardMessageDialog;
